// ============================================================================
// MANPOWER DASHBOARD - MASON FOCUSED VIEW
// Clean, professional design showing only Mason employees
// ============================================================================

import React, { useState, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { manpowerService, ManpowerProject, ManpowerEmployee } from '../../../services/manpower.service';
import ManpowerAllocationPage from './ManpowerAllocationPage';
import styles from './ManpowerDashboard.module.css';

interface ManpowerDashboardProps {
  onBack: () => void;
  onNavigate?: (page: string, params?: any) => void;
  user?: any;
  initialManagerId?: string;
}

interface EmployeeWithProject extends ManpowerEmployee {
  projectName: string;
}

const ManpowerDashboard: React.FC<ManpowerDashboardProps> = ({
  onBack,
  onNavigate,
  user,
  initialManagerId
}) => {
  // State
  const [projects, setProjects] = useState<ManpowerProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // View states
  const [currentView, setCurrentView] = useState<'dashboard' | 'masons' | 'allocation'>('dashboard');
  
  // Search & Filter for Mason view
  const [searchQuery, setSearchQuery] = useState('');
  const [filterProject, setFilterProject] = useState('All');

  // Load all projects
  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const fetchedProjects = await manpowerService.getAllProjects();
      setProjects(fetchedProjects);
      setLoading(false);
    } catch (err) {
      setError('Failed to load projects');
      setLoading(false);
    }
  };

  // Aggregate all employees with their project names
  const allEmployees = useMemo((): EmployeeWithProject[] => {
    const employees: EmployeeWithProject[] = [];
    
    projects.forEach(project => {
      project.employees.forEach(emp => {
        employees.push({
          ...emp,
          projectName: project.projectName
        });
      });
    });
    
    return employees;
  }, [projects]);

  // Get ALL Mason employees (any position starting with "Mason")
  const allMasons = useMemo(() => {
    return allEmployees.filter(emp => {
      const pos = (emp.position || '').toLowerCase();
      return pos.startsWith('mason');
    });
  }, [allEmployees]);

  // Filtered Masons based on search and project filter
  const filteredMasons = useMemo(() => {
    let filtered = allMasons;
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(emp =>
        emp.name.toLowerCase().includes(query) ||
        emp.empCode.toLowerCase().includes(query) ||
        emp.empNumber.toLowerCase().includes(query) ||
        emp.projectName.toLowerCase().includes(query) ||
        (emp.site && emp.site.toLowerCase().includes(query)) ||
        (emp.position && emp.position.toLowerCase().includes(query))
      );
    }
    
    if (filterProject !== 'All') {
      filtered = filtered.filter(emp => emp.projectName === filterProject);
    }
    
    return filtered;
  }, [allMasons, searchQuery, filterProject]);

  // Get unique projects that have Masons
  const masonProjects = useMemo(() => {
    const projectSet = new Set(allMasons.map(m => m.projectName));
    return Array.from(projectSet).sort();
  }, [allMasons]);

  // Export to Excel
  const handleExportMasons = () => {
    if (filteredMasons.length === 0) return;

    const exportData = filteredMasons.map((emp, index) => ({
      'S.NO': index + 1,
      'NAME': emp.name,
      'EMP CODE': emp.empCode,
      'EMP NUMBER': emp.empNumber,
      'PROJECT': emp.projectName,
      'PROJECT CODE': emp.projectCode || '-',
      'SITE': emp.site,
      'POSITION': emp.position,
      'DESIGNATION': emp.designation || '-',
      'DEPARTMENT': emp.department || '-',
      'NATIONALITY': emp.nationality || '-'
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Masons');
    XLSX.writeFile(workbook, `All_Masons_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Render Allocation Page
  if (currentView === 'allocation') {
    return (
      <ManpowerAllocationPage
        onBack={() => setCurrentView('dashboard')}
        onNavigate={onNavigate}
        user={user}
        initialManagerId={initialManagerId}
      />
    );
  }

  // Render Mason Detail View
  if (currentView === 'masons') {
    return (
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <button onClick={() => setCurrentView('dashboard')} className={styles.backButton}>
              <svg className={styles.backIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
              Back
            </button>
            <div className={styles.titleSection}>
              <h1 className={styles.title}>All Masons</h1>
              <span className={styles.badge}>{filteredMasons.length} of {allMasons.length}</span>
            </div>
          </div>
          <div className={styles.headerRight}>
            <button onClick={handleExportMasons} className={styles.exportButton}>
              <svg className={styles.btnIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Export Excel
            </button>
          </div>
        </div>

        {/* Filter Bar */}
        <div className={styles.filterBar}>
          <div className={styles.searchWrapper}>
            <svg className={styles.searchIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/>
              <path d="m21 21-4.35-4.35"/>
            </svg>
            <input
              type="text"
              placeholder="Search by name, code, project, site, position..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
            {searchQuery && (
              <button 
                className={styles.clearSearch}
                onClick={() => setSearchQuery('')}
              >
                ×
              </button>
            )}
          </div>
          <select
            value={filterProject}
            onChange={(e) => setFilterProject(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="All">All Projects ({masonProjects.length})</option>
            {masonProjects.map(project => (
              <option key={project} value={project}>{project}</option>
            ))}
          </select>
        </div>

        {/* Stats Row */}
        <div className={styles.statsRow}>
          <div className={styles.statPill}>
            <span className={styles.statPillLabel}>Total Masons:</span>
            <span className={styles.statPillValue}>{allMasons.length}</span>
          </div>
          <div className={styles.statPill}>
            <span className={styles.statPillLabel}>Showing:</span>
            <span className={styles.statPillValue}>{filteredMasons.length}</span>
          </div>
          <div className={styles.statPill}>
            <span className={styles.statPillLabel}>Projects:</span>
            <span className={styles.statPillValue}>{masonProjects.length}</span>
          </div>
        </div>

        {/* Employee Table */}
        <div className={styles.tableContainer}>
          {filteredMasons.length === 0 ? (
            <div className={styles.emptyState}>
              <p>No masons found matching your search</p>
            </div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>S.NO</th>
                  <th>NAME</th>
                  <th>EMP CODE</th>
                  <th>EMP NUMBER</th>
                  <th>PROJECT</th>
                  <th>SITE</th>
                  <th>POSITION</th>
                  <th>DESIGNATION</th>
                  <th>DEPARTMENT</th>
                </tr>
              </thead>
              <tbody>
                {filteredMasons.map((emp, index) => (
                  <tr key={`${emp.empCode}-${emp.projectName}-${index}`}>
                    <td className={styles.snoCell}>{index + 1}</td>
                    <td className={styles.nameCell}>{emp.name}</td>
                    <td>
                      <span className={styles.codeCell}>{emp.empCode}</span>
                    </td>
                    <td>
                      <span className={styles.codeCell}>{emp.empNumber}</span>
                    </td>
                    <td>
                      <span className={styles.projectBadge}>{emp.projectName}</span>
                    </td>
                    <td>{emp.site || '-'}</td>
                    <td>
                      <span className={styles.positionTag}>{emp.position}</span>
                    </td>
                    <td>{emp.designation || '-'}</td>
                    <td>{emp.department || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  }

  // Render Main Dashboard - Only Mason Card
  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <button onClick={onBack} className={styles.backButton}>
            <svg className={styles.backIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Back
          </button>
          <h1 className={styles.title}>Manpower Dashboard</h1>
        </div>
        <div className={styles.headerRight}>
          <button 
            onClick={() => setCurrentView('allocation')} 
            className={styles.secondaryButton}
          >
            <svg className={styles.btnIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <line x1="3" y1="9" x2="21" y2="9"/>
              <line x1="9" y1="21" x2="9" y2="9"/>
            </svg>
            Project Allocation
          </button>
          <button onClick={loadProjects} className={styles.refreshButton}>
            <svg className={styles.btnIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="23 4 23 10 17 10"/>
              <polyline points="1 20 1 14 7 14"/>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className={styles.errorAlert}>
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading manpower data...</p>
        </div>
      ) : (
        <>
          {/* Mason Card - Main Feature */}
          <div className={styles.masonSection}>
            <div 
              className={styles.masonCard}
              onClick={() => setCurrentView('masons')}
            >
              <div className={styles.masonCardLeft}>
                <div className={styles.masonIconBox}>
                  <svg className={styles.masonIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="3" y="8" width="18" height="12" rx="1"/>
                    <rect x="5" y="10" width="4" height="3"/>
                    <rect x="10" y="10" width="4" height="3"/>
                    <rect x="15" y="10" width="4" height="3"/>
                    <rect x="5" y="15" width="4" height="3"/>
                    <rect x="10" y="15" width="4" height="3"/>
                    <rect x="15" y="15" width="4" height="3"/>
                    <path d="M7 8V6a2 2 0 012-2h6a2 2 0 012 2v2"/>
                  </svg>
                </div>
                <div className={styles.masonCardInfo}>
                  <h2 className={styles.masonCardTitle}>Mason</h2>
                  <p className={styles.masonCardSubtitle}>
                    All mason types: Block, Plaster, Interlock, Tile, etc.
                  </p>
                  <div className={styles.masonCardStats}>
                    <span className={styles.masonStatItem}>
                      <strong>{allMasons.length}</strong> Total Masons
                    </span>
                    <span className={styles.masonStatDivider}>•</span>
                    <span className={styles.masonStatItem}>
                      <strong>{masonProjects.length}</strong> Projects
                    </span>
                  </div>
                </div>
              </div>
              <div className={styles.masonCardRight}>
                <div className={styles.masonCount}>{allMasons.length}</div>
                <button className={styles.viewAllBtn}>
                  View All
                  <svg className={styles.arrowIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Quick Preview Table */}
          <div className={styles.previewSection}>
            <div className={styles.previewHeader}>
              <h3>Recent Masons</h3>
              <button 
                className={styles.viewAllLink}
                onClick={() => setCurrentView('masons')}
              >
                View All {allMasons.length} Masons →
              </button>
            </div>
            
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>S.NO</th>
                    <th>NAME</th>
                    <th>EMP CODE</th>
                    <th>PROJECT</th>
                    <th>SITE</th>
                    <th>POSITION</th>
                  </tr>
                </thead>
                <tbody>
                  {allMasons.slice(0, 10).map((emp, index) => (
                    <tr key={`${emp.empCode}-${index}`}>
                      <td className={styles.snoCell}>{index + 1}</td>
                      <td className={styles.nameCell}>{emp.name}</td>
                      <td>
                        <span className={styles.codeCell}>{emp.empCode}</span>
                      </td>
                      <td>
                        <span className={styles.projectBadge}>{emp.projectName}</span>
                      </td>
                      <td>{emp.site || '-'}</td>
                      <td>
                        <span className={styles.positionTag}>{emp.position}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {allMasons.length > 10 && (
              <div className={styles.showMoreBar}>
                <button 
                  className={styles.showMoreBtn}
                  onClick={() => setCurrentView('masons')}
                >
                  Show all {allMasons.length} masons
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ManpowerDashboard;